import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { cn } from "@repo/blacksmith-ui/cn";
import { AppHeaderBar } from "./app-header-bar";
import { HeaderTitle } from "./header-title";

/** Pulls the header flush to the pane edge from inside a `px-6` page column. */
const OUTER_CLASS = "-mx-6 flex items-start justify-between pl-8 pr-6 max-md:flex-wrap";
interface SubpageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  leading?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/** Keeps the title aligned with sidebar headings, with the description below. */
export function SubpageHeader(t0) {
  const $ = _c(12);
  let description;
  let props;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      description,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = description;
    $[2] = props;
    $[3] = title;
  } else {
    description = $[1];
    props = $[2];
    title = $[3];
  }
  let t1;
  if ($[4] !== title) {
    t1 = <HeaderTitle>{title}</HeaderTitle>;
    $[4] = title;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== description) {
    t2 = description ? <p className="max-w-xl type-body-s-regular text-muted-foreground">{description}</p> : null;
    $[6] = description;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t3 = <SubpageHeaderLayout {...props} title={t1} description={t2} />;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export function SubpageHeaderSkeleton(props) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-6 w-40 rounded" />;
    t1 = <Skeleton className="h-4 w-72 rounded" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <SubpageHeaderLayout {...props} title={t0} description={t1} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/** Breadcrumb row with an optional leading control. */
export function SubpageCrumbRow(t0) {
  const $ = _c(6);
  const {
    children,
    leading,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== leading || $[4] !== t1) {
    t2 = <AppHeaderBar className={t1}>{leading}{children}</AppHeaderBar>;
    $[2] = children;
    $[3] = leading;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/** `empty:hidden` so a null toggle doesn't hold the row's `gap-2`. */
function SubpageHeaderLayout(t0) {
  const $ = _c(18);
  const {
    title,
    description,
    leading,
    actions,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(OUTER_CLASS, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== leading) {
    t2 = leading ? <AppHeaderBar className="empty:hidden">{leading}</AppHeaderBar> : null;
    $[2] = leading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <AppHeaderBar>{title}</AppHeaderBar>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== description || $[7] !== t3) {
    t4 = <div>{t3}{description}</div>;
    $[6] = description;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex min-w-0 items-start gap-2">{t2}{t4}</div>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== actions) {
    t6 = actions ? <AppHeaderBar className="gap-2">{actions}</AppHeaderBar> : null;
    $[12] = actions;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t5 || $[16] !== t6) {
    t7 = <div className={t1}>{t5}{t6}</div>;
    $[14] = t1;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}